import { Link } from 'react-router-dom';
import {
  BookOpen,
  BookUser,
  Building2,
  CalendarCheck,
  FolderOpen,
  HandHelping,
  LayoutDashboard,
  LogIn,
  Mail,
  MessagesSquare,
  QrCode,
  Scale,
  ShieldOff,
  Filter,
  Send,
  Shield,
  Tent,
  ShoppingBag,
  ShoppingCart,
  Truck,
  Users,
  UsersRound,
} from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { fetchCompteursAccesRapide, fetchNonLusBoitesMail } from '../services/api';
import { useAuth } from '../hooks/useAuth';
import { usePersonnalisation } from '../hooks/usePersonnalisation';
import AlerteEcheances from '../components/AlerteEcheances';

/**
 * Une couleur par métier, et non par carte.
 *
 * Douze vignettes du même bleu se lisent une à une, en déchiffrant chaque
 * libellé. Trois familles de couleurs se repèrent avant d'être lues : on sait
 * dans quel tiers de l'écran chercher avant d'y avoir posé les yeux.
 *
 * La couleur dit ce dont il s'agit, pas ce qui est important — il n'y a rien
 * d'urgent ici, ce sont des raccourcis. Ce qui presse porte une pastille.
 */
const TEINTES = {
  // Ce qui se fait avec des gens : annuaire, messages, réunions, missions.
  vie: 'text-sky-600 dark:text-sky-400',
  // Ce qui s'organise : événements, planning, documents, diffusion.
  organisation: 'text-violet-600 dark:text-violet-400',
  // Ce qui compte l'argent : bilan, ventes, achats.
  argent: 'text-emerald-600 dark:text-emerald-400',
  // Ce qui tient les fiches : membres, sponsors, fournisseurs.
  registres: 'text-amber-600 dark:text-amber-400',
  // La boutique, qui n'est ni tout à fait de l'argent ni tout à fait un
  // registre : elle a sa propre teinte plutôt qu'une approximation.
  boutique: 'text-rose-600 dark:text-rose-400',
} as const;

/**
 * Accessible à tout membre connecté.
 *
 * « Membres » a rejoint la gestion : consulter l'annuaire se fait par le
 * Répertoire, tenir la liste est un acte de bureau. Les deux cartes menaient
 * au même écran sous deux noms, ce qui obligeait à choisir sans savoir.
 */
const modulesBenevole = [
  { to: '/repertoire/association', label: 'Répertoire', icone: BookUser, teinte: TEINTES.vie },
  // La messagerie de fonction : ce qu'on ouvre en arrivant, avant le reste.
  { to: '/messagerie', label: 'Messagerie', icone: Mail, teinte: TEINTES.vie, badge: 'mails' },
  { to: '/bureau/reunions', label: 'Réunions', icone: MessagesSquare, teinte: TEINTES.vie, badge: 'reunions' },
  { to: '/mes-missions', label: 'Mes missions', icone: HandHelping, teinte: TEINTES.vie, badge: 'missions' },
];

/**
 * Modules de gestion, rangés par métier.
 *
 * Devis, factures et avoirs sont retirés : ils ouvrent tous trois l'écran de
 * facturation des sponsors, que la carte « Sponsors » ouvre déjà. Quatre
 * raccourcis vers le même écran obligent à choisir entre des portes
 * identiques.
 *
 * « Planning » est retiré pour la même raison : le planning se tient dans
 * l'événement, et la carte menait à la liste des événements.
 */
/**
 * LES ADHÉRENTS AVANT LES MEMBRES, ET C'EST L'ORDRE D'USAGE.
 *
 * Ce sont deux populations distinctes, et la seconde est bien plus petite :
 * un club de deux cents licenciés a huit membres de bureau. On ouvre le
 * registre des adhérents chaque semaine — une inscription, un certificat qui
 * manque, une cotisation en retard — et l'annuaire des membres deux fois par
 * an. Mettre le petit registre devant le grand obligeait à le dépasser des
 * yeux à chaque visite.
 *
 * ELLE VIT HORS DE LA LISTE
 * ---------------------------------------------------------------------------
 * Sa destination dépend de l'écran : le sommaire du registre sur téléphone,
 * le répertoire directement sur ordinateur, où les six écrans sont déjà
 * dépliés dans la barre de gauche. Une entrée de liste ne sait pas exprimer
 * cela — elle porte une adresse, pas deux.
 *
 * ELLE SUIT SON OPTION ET SON DROIT
 * ---------------------------------------------------------------------------
 * Sans le module « licences », l'écran n'existe pas : la carte mènerait à une
 * page vide. Sans le droit « voir_adherents », elle mènerait à un refus — et
 * une porte fermée à clé est pire qu'une porte absente, parce qu'on la pousse.
 */
const CARTE_ADHERENTS = { option: 'licences', droit: 'voir_adherents' };

const modules = [
  // Organiser : la manifestation, ce qu'elle demande, ce qu'on en dit.
  { to: '/bureau/evenements', label: 'Événements', icone: CalendarCheck, teinte: TEINTES.organisation },
  { to: '/bureau/documents', label: 'Documents', icone: FolderOpen, teinte: TEINTES.organisation, badge: 'documents' },
  { to: '/bureau/diffusion', label: 'Diffusion', icone: Send, teinte: TEINTES.organisation },
  // Le même droit que « Diffusion », un autre geste : cinq listes toutes
  // faites d'un côté, « ceux qui doivent encore quelque chose » de l'autre.
  // La sélection ciblée appartient au module Festival : sans lui, pas de carte.
  { to: '/bureau/campagnes', label: 'Sélection ciblée', icone: Filter, teinte: TEINTES.organisation, option: 'festival', droit: 'diffuser_messages' },
  // Le festival : module optionnel — la carte suit l'option ET le droit.
  { to: '/bureau/festival', label: 'Festival', icone: Tent, teinte: TEINTES.organisation, option: 'festival', droit: 'voir_festival' },
  { to: '/bureau/assurance', label: 'Assurances', icone: Shield, teinte: TEINTES.organisation, option: 'assurance', droit: 'voir_assurance' },

  // Compter : ce qui rentre, ce qui sort, et où l'on en est.
  { to: '/bureau/bilan', label: 'Bilan', icone: Scale, teinte: TEINTES.argent },
  { to: '/bureau/tableau-de-bord', label: 'Suivi ventes', icone: LayoutDashboard, teinte: TEINTES.argent },
  { to: '/bureau/achats', label: 'Suivi achats', icone: ShoppingCart, teinte: TEINTES.argent },

  // Tenir les fiches : les personnes et les entreprises.
  // « Adhérents » est rendue à part, juste avant celle-ci : sa destination
  // dépend de l'écran, ce qu'une entrée de liste ne sait pas exprimer.
  { to: '/bureau/membres', label: 'Membres', icone: Users, teinte: TEINTES.registres },
  { to: '/sponsors', label: 'Sponsors', icone: Building2, teinte: TEINTES.registres },
  { to: '/repertoire/fournisseurs', label: 'Fournisseurs', icone: Truck, teinte: TEINTES.registres },

  { to: '/admin/boutique', label: 'Boutique', icone: ShoppingBag, teinte: TEINTES.boutique },
];

/**
 * Les raccourcis du membre, rassemblés sur un écran à eux.
 *
 * Ils occupaient le bas de « Mon espace », qui devenait un fourre-tout : on y
 * cherchait ses coordonnées et on tombait sur la facturation. Séparés, chacun
 * des deux écrans dit ce qu'il est.
 */
/**
 * Une pastille sur une carte : ce qui attend, et rien d'autre.
 *
 * Réservée à ce qui appelle un geste — des missions à confirmer, un sondage
 * auquel répondre, des documents qu'on n'a pas encore vus. Une pastille sur
 * ce qui se consulte simplement apprendrait à l'ignorer, et elle ne servirait
 * plus nulle part.
 */
function Pastille({ valeur, ton = 'info' }: { valeur: number; ton?: 'info' | 'attention' }) {
  if (valeur <= 0) return null;

  return (
    <span
      className={`absolute top-2 right-2 min-w-[1.25rem] px-1.5 py-0.5 rounded-full text-[0.65rem] font-bold leading-tight text-white ${
        ton === 'attention' ? 'bg-amber-500' : 'bg-primary-600'
      }`}
    >
      {valeur > 99 ? '99+' : valeur}
    </span>
  );
}

export default function AccesRapide() {
  const { user, isLoading, aLeDroit } = useAuth();
  const perso = usePersonnalisation();

  /*
    Une option non lue n'efface rien.

    Tant que la personnalisation n'est pas revenue, « options » est absent. Le
    défaut est donc OUVERT : une carte qui disparaît une demi-seconde puis
    revient se remarque bien plus qu'une carte qui attend.
  */
  const optionOuverte = (nom: string) => (perso?.options as Record<string, boolean> | undefined)?.[nom] !== false;

  const carteVisible = (m: { option?: string; droit?: string }) =>
    (m.option === undefined || optionOuverte(m.option))
    && (m.droit === undefined || aLeDroit(m.droit));

  // Les nombres des pastilles, en une seule requête. Les boîtes mail sont à
  // part : elles passent par IMAP, et une messagerie lente ne doit pas retenir
  // les quatre autres compteurs.
  const compteurs = useQuery({
    queryKey: ['acces-rapide-compteurs'],
    queryFn: fetchCompteursAccesRapide,
    enabled: !!user,
    staleTime: 60_000,
  });

  const mails = useQuery({
    queryKey: ['boites-mail-non-lus'],
    queryFn: fetchNonLusBoitesMail,
    enabled: !!user,
    staleTime: 60_000,
    retry: false,
  });

  const nonLus = Object.values(mails.data?.nonLus ?? {}).reduce<number>(
    (total, n) => total + (typeof n === 'number' ? n : 0),
    0
  );

  const c = compteurs.data;
  const sondages = c?.reunions.sondages ?? 0;
  const planifiees = c?.reunions.planifiees ?? 0;

  /** Le nombre que porte une carte, et le ton de sa pastille. */
  const pastilleDe = (code?: string): { valeur: number; ton: 'info' | 'attention' } => {
    switch (code) {
      case 'missions':
        return { valeur: c?.missions ?? 0, ton: 'info' };
      case 'mails':
        return { valeur: nonLus, ton: 'info' };
      case 'documents':
        return { valeur: c?.documentsRecents ?? 0, ton: 'info' };
      // Un sondage attend une réponse de ma part : c'est le seul des deux qui
      // appelle un geste, d'où l'ambre. Une réunion arrêtée attend seulement
      // ma présence.
      case 'reunions':
        return sondages > 0
          ? { valeur: sondages, ton: 'attention' }
          : { valeur: planifiees, ton: 'info' };
      default:
        return { valeur: 0, ton: 'info' };
    }
  };

  if (isLoading) return null;

  if (!user) {
    return (
      <div className="p-4 lg:p-0 space-y-4 pb-24 lg:pb-0">
        <h1 className="text-2xl lg:text-3xl font-bold text-gray-900 dark:text-gray-100">
          Accès rapide
        </h1>
        <p className="text-gray-600 dark:text-gray-400">
          Connectez-vous pour retrouver vos raccourcis.
        </p>
        <Link to="/login" className="btn-primary w-full lg:w-auto">
          <LogIn className="w-5 h-5" />
          Se connecter
        </Link>
      </div>
    );
  }

  const estBureau =
    user.roles?.includes('ROLE_BUREAU') === true || user.roles?.includes('ROLE_ADMIN') === true;
  const estBenevole = user.roles?.includes('ROLE_VOLUNTEER') === true || estBureau;

  // Un sponsor n'a rien à faire ici : tout ce qu'il ouvrirait lui serait
  // refusé. Le lien ne lui est pas montré, mais l'adresse se tape.
  if (!estBenevole) {
    return (
      <div className="p-4 lg:p-0 space-y-4 pb-24 lg:pb-0">
        <h1 className="text-2xl lg:text-3xl font-bold text-gray-900 dark:text-gray-100">
          Accès rapide
        </h1>
        <div className="card flex items-start gap-3">
          <ShieldOff className="w-5 h-5 mt-0.5 text-gray-400 shrink-0" />
          <div>
            <p className="font-medium text-gray-900 dark:text-gray-100">
              Réservé aux bénévoles et au bureau
            </p>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              Vos documents — devis et factures — vous attendent dans Mon espace.
            </p>
          </div>
        </div>
        <Link to="/informations" className="btn-primary w-full lg:w-auto">
          Aller à Mon espace
        </Link>
      </div>
    );
  }

  return (
    <div className="p-4 lg:p-0 space-y-5 lg:space-y-6 pb-24 lg:pb-0">
      <h1 className="text-2xl lg:text-3xl font-bold text-gray-900 dark:text-gray-100">
        Accès rapide
      </h1>

      {/* Ce qui arrive à terme dans la bibliothèque. Ici et pas ailleurs :
          c'est l'écran qu'on ouvre en arrivant, et une alerte qu'il faut aller
          chercher n'est pas une alerte. */}
      <AlerteEcheances />

      <section className="space-y-3">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 lg:gap-4">
          {modulesBenevole.map((m) => {
            const p = pastilleDe(m.badge);
            return (
              <Link
                key={m.label}
                to={m.to}
                className="card relative flex flex-col items-center gap-2 hover:shadow-md transition lg:p-6 text-center"
              >
                <Pastille valeur={p.valeur} ton={p.ton} />
                <m.icone className={`w-6 h-6 ${m.teinte}`} />
                <span className="text-sm font-medium">{m.label}</span>
                {m.badge === 'reunions' && (sondages > 0 || planifiees > 0) && (
                  <span className="text-[0.65rem] text-gray-500 dark:text-gray-400 leading-tight">
                    {sondages > 0 && `${sondages} sondage${sondages > 1 ? 's' : ''}`}
                    {sondages > 0 && planifiees > 0 && ' · '}
                    {planifiees > 0 && `${planifiees} réunion${planifiees > 1 ? 's' : ''}`}
                  </span>
                )}
              </Link>
            );
          })}
        </div>

        {/* Deuxième ligne, deux cartes seulement et plus larges. Ce sont les
            deux gestes qui n'ont rien à voir avec les autres : l'un se fait
            debout devant une file d'attente, l'autre quand on ne sait pas
            faire quelque chose. Les noyer parmi huit vignettes de la même
            taille revenait à les rendre introuvables au moment où l'on en a
            besoin. */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 lg:gap-4">
          {estBureau && (
            <Link
              to="/retrait-repas"
              className="card flex items-center gap-4 hover:shadow-md transition p-5 lg:p-6 bg-primary-600 text-white border-primary-600"
            >
              <QrCode className="w-10 h-10 shrink-0" />
              <span className="min-w-0">
                <span className="block text-base lg:text-lg font-semibold">Scan QR Code</span>
                <span className="block text-xs opacity-85 leading-snug mt-0.5">
                  Remise des tickets et des repas, caméra ouverte à l'arrivée
                </span>
              </span>
            </Link>
          )}

          <Link
            to="/guides"
            className="card flex items-center gap-4 hover:shadow-md transition p-5 lg:p-6"
          >
            <BookOpen className="w-10 h-10 text-primary-600 shrink-0" />
            <span className="min-w-0">
              <span className="block text-base lg:text-lg font-semibold text-gray-900 dark:text-gray-100">
                Guides
              </span>
              <span className="block text-xs text-gray-600 dark:text-gray-400 leading-snug mt-0.5">
                Comment faire, écran par écran — chacun n'y voit que ce qui le concerne
              </span>
            </span>
          </Link>
        </div>
      </section>

      {(user.accesModules || user.peutGererEvenements) && (
        <section className="space-y-3">
          <h2 className="font-semibold lg:text-lg text-gray-900 dark:text-gray-100">Gestion</h2>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 lg:gap-4">
            {/* Les événements figurent déjà dans la liste ci-dessous. Cette
                carte ne sert qu'à qui gère les manifestations sans avoir le
                reste de la gestion — sans cette condition, les deux
                s'affichaient côte à côte, identiques. */}
            {user.peutGererEvenements && !user.accesModules && (
              <Link
                to="/bureau/evenements"
                className="card flex flex-col items-center gap-2 hover:shadow-md transition lg:p-6 text-center"
              >
                <CalendarCheck className={`w-6 h-6 ${TEINTES.organisation}`} />
                <span className="text-sm font-medium">Événements</span>
              </Link>
            )}
            {/*
              LA CARTE DES ADHÉRENTS MÈNE AILLEURS SELON L'ÉCRAN.

              Sur ordinateur, les six écrans du registre sont dépliés en
              permanence dans la barre de gauche : un sommaire n'apprendrait
              rien et ajouterait un clic. On va donc droit au répertoire.

              Sur téléphone, il n'y a pas de barre latérale — et les cinq
              autres écrans n'étaient atteignables qu'en naviguant depuis le
              répertoire. La carte ouvre donc un sommaire.

              DEUX LIENS PLUTÔT QU'UNE MESURE DE FENÊTRE
              ------------------------------------------------------------
              L'application monte SES DEUX mises en page et en cache une par
              CSS. Cette page est donc rendue deux fois, et la même règle qui
              choisit la barre choisit ici la destination : rien à mesurer,
              rien à recalculer au redimensionnement, et aucun écart possible
              entre ce qu'on voit et ce qui répond.
            */}
            {user.accesModules && carteVisible(CARTE_ADHERENTS) && (
              <>
                <Link
                  to="/adherents/acces"
                  className="card lg:hidden flex flex-col items-center gap-2 hover:shadow-md transition text-center"
                >
                  <UsersRound className={`w-6 h-6 ${TEINTES.registres}`} />
                  <span className="text-sm font-medium">Adhérents</span>
                </Link>
                <Link
                  to="/adherents"
                  className="card hidden lg:flex flex-col items-center gap-2 hover:shadow-md transition lg:p-6 text-center"
                >
                  <UsersRound className={`w-6 h-6 ${TEINTES.registres}`} />
                  <span className="text-sm font-medium">Adhérents</span>
                </Link>
              </>
            )}
            {user.accesModules &&
              modules.map((m) => {
                const p = pastilleDe((m as { badge?: string }).badge);
                return (
                  <Link
                    key={m.label}
                    to={m.to}
                    className="card relative flex flex-col items-center gap-2 hover:shadow-md transition lg:p-6 text-center"
                  >
                    <Pastille valeur={p.valeur} ton={p.ton} />
                    <m.icone className={`w-6 h-6 ${m.teinte}`} />
                    <span className="text-sm font-medium">{m.label}</span>
                  </Link>
                );
              })}
          </div>
        </section>
      )}
    </div>
  );
}
